import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Shield, Search, Trash2, AlertCircle, Loader2, 
  User, Mail, Building, Calendar, Eye, BarChart3, TrendingUp,
  UserCircle, Monitor, Smartphone, Tablet, Globe, MapPin, Clock, ArrowRight,
  ChevronDown, ChevronUp, Settings, Activity, Users, CreditCard, FileText, DollarSign, UserCog, Plus, Edit, X, MessageCircle, Scan, Palette, Megaphone, Layers
} from 'lucide-react';
import AdminCardDesigner from './AdminCardDesigner';
import AdminMarketing from '../components/AdminMarketing';
import AdminBioTemplates from '../components/AdminBioTemplates';
import { useAuth } from '../contexts/AuthContext';
import { useUserCard } from '../hooks/useUserCard';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import DesignCardPreview from '../components/DesignCardPreview';

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

// Component to fetch and display viewer's card button (copied from Profile.js)
const ViewerCardButton = ({ viewerId }) => {
  const [viewerCard, setViewerCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchViewerCard = async () => {
      if (!viewerId) return;
      
      try {
        setLoading(true);
        const { cardAPI } = await import('../services/api');
        const card = await cardAPI.getByUserId(viewerId);
        setViewerCard(card);
      } catch (error) {
        console.error('Error fetching viewer card:', error);
        setViewerCard(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchViewerCard();
  }, [viewerId]);

  if (loading) return null;
  if (!viewerCard) return null;

  return (
    <Button
      size="sm"
      className="h-6 px-2 text-xs bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white border-0"
      onClick={() => window.open(`/bio/${viewerCard.username}`, '_blank')}
    >
      View Card
    </Button>
  );
};

// Format date helper
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
  });
};

const Admin = () => {
  const { user } = useAuth();
  const { userCard, loading: cardLoading } = useUserCard();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [activeTab, setActiveTab] = useState('coupons');
  const [anonymousSectionOpen, setAnonymousSectionOpen] = useState(false);
  const [userDesigns, setUserDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [previewDesign, setPreviewDesign] = useState(null);
  const [previewSide, setPreviewSide] = useState('front');
  const [pricingEnabled, setPricingEnabled] = useState(true);
  const [premiumEnabled, setPremiumEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [maxScansPerUser, setMaxScansPerUser] = useState(10);
  const [contactEmail, setContactEmail] = useState('contact@allgrowtechnologies.com');
  const [loadingAppSettings, setLoadingAppSettings] = useState(false);
  
  // Backgrounds state
  const [adminBackgrounds, setAdminBackgrounds] = useState([]);
  const [loadingBackgrounds, setLoadingBackgrounds] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    percentage: '',
    tier: 'all',
    billingPeriod: 'both',
    minMembers: '',
    description: '',
    expiryDate: '',
    usageLimit: '',
    minOrderValue: '',
    oneTimePerUser: false,
    isActive: true
  });
  const [userSubscriptions, setUserSubscriptions] = useState({});
  const [updatingSubscription, setUpdatingSubscription] = useState({});
  
  // Country Pricing states
  const [countryPricings, setCountryPricings] = useState([]);
  const [loadingCountryPricing, setLoadingCountryPricing] = useState(false);
  const [newCountryPricing, setNewCountryPricing] = useState({
    countryCode: '',
    countryName: '',
    proMonthly: '',
    proAnnual: '',
    businessMonthly: '',
    businessAnnual: ''
  });
  const [editingCountry, setEditingCountry] = useState(null);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  
  // Card Pricing states (NFC and Paper Cards)
  const [cardPricing, setCardPricing] = useState({
    nfc_mrp: 1499,
    nfc_price: 999,
    paper_100: 499,
    paper_200: 899,
    paper_300: 1199,
    paper_500: 1799,
    paper_1000: 2999
  });
  const [loadingCardPricing, setLoadingCardPricing] = useState(false);
  const [savingCardPricing, setSavingCardPricing] = useState(false);

  // Check if user is admin
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  // Load settings from localStorage
  useEffect(() => {
    const savedPricingState = localStorage.getItem('pricingEnabled');
    if (savedPricingState !== null) {
      setPricingEnabled(JSON.parse(savedPricingState));
    }
    
    const savedPremiumState = localStorage.getItem('premiumEnabled');
    if (savedPremiumState !== null) {
      setPremiumEnabled(JSON.parse(savedPremiumState));
    }
  }, []);

  // Load app settings from backend
  useEffect(() => {
    const loadAppSettings = async () => {
      if (!user || activeTab !== 'settings') return;
      
      try {
        setLoadingAppSettings(true);
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/app`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setMaxScansPerUser(data.max_scans_per_user);
          setContactEmail(data.contact_email);
        }
      } catch (error) {
        console.error('Error loading app settings:', error);
      } finally {
        setLoadingAppSettings(false);
      }
    };
    
    loadAppSettings();
  }, [user, activeTab]);

  useEffect(() => {
    // Prevent duplicate calls
    let isActive = true;

    const initAdmin = async () => {
      if (!cardLoading && (!user || !isAdmin)) {
        if (isActive) {
          toast({
            title: "Access Denied",
            description: "You don't have permission to access this page.",
            variant: "destructive"
          });
          navigate('/');
        }
        return;
      }

      if (user && isAdmin && isActive) {
        try {
          setLoading(true);
          // Only load subscriptions (users list feature removed)
          await loadUserSubscriptions();
        } finally {
          if (isActive) {
            setLoading(false);
          }
        }
      }
    };

    initAdmin();

    return () => {
      isActive = false;
    };
  }, [user?.uid, isAdmin, cardLoading]); // Only essential dependencies

  // Load analytics when views tab is active and cards are loaded
  // Load coupons when coupons tab is active
  useEffect(() => {
    if (activeTab === 'coupons' && user) {
      loadCoupons();
    }
  }, [activeTab, user]);

  // Load country pricing when pricing tab is active
  useEffect(() => {
    if (activeTab === 'pricing' && user) {
      loadCountryPricing();
    }
  }, [activeTab, user]);

  // Load user designs when designs tab is active
  useEffect(() => {
    if (activeTab === 'user-designs' && user) {
      loadUserDesigns();
    }
  }, [activeTab, user]);

  const deleteUserDesign = async (designId) => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/card-designer/admin/user-designs/${designId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setUserDesigns(prev => prev.filter(d => d.id !== designId));
      }
    } catch (e) { console.error('Delete failed', e); }
  };

  const loadUserDesigns = async () => {
    setLoadingDesigns(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/card-designer/admin/user-designs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setUserDesigns(await res.json());
    } catch (e) { console.error('Failed to load designs', e); }
    finally { setLoadingDesigns(false); }
  };

  // Load backgrounds when backgrounds tab is active
  useEffect(() => {
    if (activeTab === 'backgrounds' && user) {
      loadAdminBackgrounds();
    }
  }, [activeTab, user]);

  // Load Card Pricing when card-pricing tab is active
  useEffect(() => {
    if (activeTab === 'card-pricing' && user) {
      loadCardPricing();
    }
  }, [activeTab, user]);

  // Load Card Pricing function
  const loadCardPricing = async () => {
    try {
      setLoadingCardPricing(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/global-pricing`);
      if (response.ok) {
        const data = await response.json();
        setCardPricing(data);
      }
    } catch (error) {
      console.error('Error loading card pricing:', error);
    } finally {
      setLoadingCardPricing(false);
    }
  };

  // Save Card Pricing function
  const saveCardPricing = async () => {
    if (!user) return;
    
    try {
      setSavingCardPricing(true);
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/admin/global-pricing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cardPricing)
      });
      
      if (response.ok) {
        toast({
          title: 'Card Pricing Updated',
          description: 'NFC and paper card prices have been saved successfully',
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to update pricing',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error saving card pricing:', error);
      toast({
        title: 'Error',
        description: 'Failed to save card pricing',
        variant: 'destructive'
      });
    } finally {
      setSavingCardPricing(false);
    }
  };

  const loadAdminBackgrounds = async () => {
    setLoadingBackgrounds(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backgrounds/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAdminBackgrounds(await res.json());
    } catch (e) { console.error('Failed to load backgrounds', e); }
    finally { setLoadingBackgrounds(false); }
  };

  const uploadBackground = async (file) => {
    setUploadingBackground(true);
    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backgrounds/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast({ title: 'Success', description: 'Background uploaded successfully' });
        loadAdminBackgrounds(); // Refresh list
        return data;
      } else {
        const error = await res.json();
        toast({ title: 'Error', description: error.detail || 'Failed to upload', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Upload failed', e);
      toast({ title: 'Error', description: 'Failed to upload background', variant: 'destructive' });
    } finally {
      setUploadingBackground(false);
    }
  };

  const deleteBackground = async (bgId) => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backgrounds/admin/${bgId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Background deleted' });
        loadAdminBackgrounds();
      } else {
        const error = await res.json();
        toast({ title: 'Error', description: error.detail || 'Cannot delete this background', variant: 'destructive' });
      }
    } catch (e) {
      console.error('Delete failed', e);
      toast({ title: 'Error', description: 'Failed to delete background', variant: 'destructive' });
    }
  };



  // loadAllUsers function removed - Users list feature removed from admin panel

  const loadUserSubscriptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/admin/users-subscriptions`, {
        headers: {
          'X-Admin-Email': user?.email || ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        const subscriptionMap = {};
        data.users.forEach(u => {
          subscriptionMap[u.userId] = {
            tier: u.subscriptionTier || 'free',
            status: u.subscriptionStatus || 'inactive',
            cardLimit: u.cardLimit || 1,
            startDate: u.startDate,
            endDate: u.endDate
          };
        });
        setUserSubscriptions(subscriptionMap);
      }
    } catch (error) {
      console.error('Error loading user subscriptions:', error);
    }
  };

  const updateUserSubscription = async (userId, newTier) => {
    try {
      setUpdatingSubscription(prev => ({ ...prev, [userId]: true }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/admin/update-subscription/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': user?.email || ''
        },
        body: JSON.stringify({
          tier: newTier,
          duration_days: 365
        })
      });

      if (response.ok) {
        toast({
          title: "Subscription Updated",
          description: `User subscription changed to ${newTier.toUpperCase()}`,
        });
        
        // Reload subscriptions
        await loadUserSubscriptions();
      } else {
        throw new Error('Failed to update subscription');
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast({
        title: "Error",
        description: "Failed to update subscription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUpdatingSubscription(prev => ({ ...prev, [userId]: false }));
    }
  };

  const loadCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      } else {
        throw new Error('Failed to load coupons');
      }
    } catch (error) {
      console.error('Error loading coupons:', error);
      toast({
        title: "Error",
        description: "Failed to load coupons.",
        variant: "destructive"
      });
    } finally {
      setLoadingCoupons(false);
    }
  };

  const createCoupon = async () => {
    try {
      // Validation
      if (!newCoupon.code || !newCoupon.percentage || !newCoupon.tier) {
        toast({
          title: "Validation Error",
          description: "Coupon code, percentage, and tier are required.",
          variant: "destructive"
        });
        return;
      }

      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: newCoupon.code,
          percentage: parseInt(newCoupon.percentage),
          tier: newCoupon.tier,
          billingPeriod: newCoupon.billingPeriod,
          minMembers: newCoupon.minMembers ? parseInt(newCoupon.minMembers) : null,
          description: newCoupon.description || null,
          expiryDate: newCoupon.expiryDate || null,
          usageLimit: newCoupon.usageLimit ? parseInt(newCoupon.usageLimit) : null,
          minOrderValue: newCoupon.minOrderValue ? parseInt(newCoupon.minOrderValue) : null,
          isActive: newCoupon.isActive
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Coupon created successfully!"
        });
        setNewCoupon({
          code: '',
          percentage: '',
          tier: 'all',
          billingPeriod: 'both',
          minMembers: '',
          description: '',
          expiryDate: '',
          usageLimit: '',
          minOrderValue: '',
          isActive: true
        });
        loadCoupons();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create coupon');
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create coupon.",
        variant: "destructive"
      });
    }
  };

  const toggleCouponStatus = async (couponId, currentStatus) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/${couponId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: `Coupon ${!currentStatus ? 'activated' : 'deactivated'} successfully!`
        });
        loadCoupons();
      } else {
        throw new Error('Failed to update coupon');
      }
    } catch (error) {
      console.error('Error updating coupon:', error);
      toast({
        title: "Error",
        description: "Failed to update coupon status.",
        variant: "destructive"
      });
    }
  };

  const deleteCoupon = async (couponId) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/${couponId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Coupon deleted successfully!"
        });
        loadCoupons();
      } else {
        throw new Error('Failed to delete coupon');
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast({
        title: "Error",
        description: "Failed to delete coupon.",
        variant: "destructive"
      });
    }
  };

  // Country Pricing Functions
  const loadCountryPricing = async () => {
    try {
      setLoadingCountryPricing(true);
      const { countryPricingAPI } = await import('../services/api');
      const data = await countryPricingAPI.adminGetAllCountries();
      setCountryPricings(data);
    } catch (error) {
      console.error('Error loading country pricing:', error);
      toast({
        title: "Error",
        description: "Failed to load country pricing.",
        variant: "destructive"
      });
    } finally {
      setLoadingCountryPricing(false);
    }
  };

  const createOrUpdateCountryPricing = async () => {
    try {
      // Validation
      if (!newCountryPricing.countryCode || !newCountryPricing.countryName ||
          !newCountryPricing.proMonthly || !newCountryPricing.proAnnual ||
          !newCountryPricing.businessMonthly || !newCountryPricing.businessAnnual) {
        toast({
          title: "Validation Error",
          description: "All fields are required.",
          variant: "destructive"
        });
        return;
      }

      // Validate country code length (2 for ISO codes, 3 for ROW - Rest of World)
      const countryCodeUpper = newCountryPricing.countryCode.toUpperCase().trim();
      if (countryCodeUpper !== 'ROW' && countryCodeUpper.length !== 2) {
        toast({
          title: "Validation Error",
          description: "Country code must be exactly 2 characters (e.g., US, GB) or ROW for Rest of World.",
          variant: "destructive"
        });
        return;
      }

      // Validate numeric fields
      const proMonthly = parseFloat(newCountryPricing.proMonthly);
      const proAnnual = parseFloat(newCountryPricing.proAnnual);
      const businessMonthly = parseFloat(newCountryPricing.businessMonthly);
      const businessAnnual = parseFloat(newCountryPricing.businessAnnual);

      if (isNaN(proMonthly) || isNaN(proAnnual) || isNaN(businessMonthly) || isNaN(businessAnnual)) {
        toast({
          title: "Validation Error",
          description: "All price fields must be valid numbers.",
          variant: "destructive"
        });
        return;
      }

      if (proMonthly < 0 || proAnnual < 0 || businessMonthly < 0 || businessAnnual < 0) {
        toast({
          title: "Validation Error",
          description: "Prices cannot be negative.",
          variant: "destructive"
        });
        return;
      }

      const { countryPricingAPI } = await import('../services/api');
      
      const pricingData = {
        countryCode: newCountryPricing.countryCode.toUpperCase().trim(),
        countryName: newCountryPricing.countryName.trim(),
        proMonthly: Math.round(proMonthly * 100), // Convert to paise
        proAnnual: Math.round(proAnnual * 100),
        businessMonthly: Math.round(businessMonthly * 100),
        businessAnnual: Math.round(businessAnnual * 100),
        isActive: true
      };

      console.log('Sending pricing data:', pricingData);

      await countryPricingAPI.adminCreateCountry(pricingData);
      
      toast({
        title: "Success",
        description: `Country pricing ${editingCountry ? 'updated' : 'created'} successfully!`
      });
      
      setNewCountryPricing({
        countryCode: '',
        countryName: '',
        proMonthly: '',
        proAnnual: '',
        businessMonthly: '',
        businessAnnual: ''
      });
      setEditingCountry(null);
      loadCountryPricing();
    } catch (error) {
      console.error('Error creating/updating country pricing:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to save country pricing.",
        variant: "destructive"
      });
    }
  };

  const editCountryPricing = (country) => {
    setEditingCountry(country);
    setNewCountryPricing({
      countryCode: country.countryCode,
      countryName: country.countryName,
      proMonthly: (country.proMonthly / 100).toString(),
      proAnnual: (country.proAnnual / 100).toString(),
      businessMonthly: (country.businessMonthly / 100).toString(),
      businessAnnual: (country.businessAnnual / 100).toString()
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteCountryPricing = async (countryCode) => {
    try {
      const { countryPricingAPI } = await import('../services/api');
      await countryPricingAPI.adminDeleteCountry(countryCode);
      
      toast({
        title: "Success",
        description: "Country pricing deleted successfully!"
      });
      loadCountryPricing();
    } catch (error) {
      console.error('Error deleting country pricing:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete country pricing.",
        variant: "destructive"
      });
    }
  };

  const cancelEdit = () => {
    setEditingCountry(null);
    setNewCountryPricing({
      countryCode: '',
      countryName: '',
      proMonthly: '',
      proAnnual: '',
      businessMonthly: '',
      businessAnnual: ''
    });
  };

  const handleDeleteClick = (card) => {
    setCardToDelete(card);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cardToDelete) return;

    try {
      setDeleting(true);
      const { cardAPI } = await import('../services/api');
      await cardAPI.delete(cardToDelete.username);
      
      const displayName = cardToDelete.name || `@${cardToDelete.username}` || 'Unknown user';
      
      toast({
        title: "Card Deleted",
        description: `Successfully deleted card for ${displayName}`,
      });
      
      // Invalidate cache for card deletion
      try {
        const cacheManager = (await import('../utils/cache')).default;
        cacheManager.clearAll();
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
      
      // Refresh the list
      setCards(cards.filter(c => c.username !== cardToDelete.username));
      setDeleteDialogOpen(false);
      setCardToDelete(null);
    } catch (error) {
      console.error('Error deleting card:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete card. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteUserClick = (user) => {
    setUserToDelete(user);
    setDeleteUserDialogOpen(true);
  };

  const handleDeleteUserConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeletingUser(true);
      const { cardAPI } = await import('../services/api');
      
      // Delete all cards for this user
      let successCount = 0;
      let errorCount = 0;
      
      for (const card of userToDelete.cards) {
        try {
          await cardAPI.delete(card.username);
          successCount++;
        } catch (error) {
          console.error(`Error deleting card ${card.username}:`, error);
          errorCount++;
        }
      }
      
      if (errorCount === 0) {
        toast({
          title: "User Deleted",
          description: `Successfully deleted ${userToDelete.userName || 'user'} and all ${successCount} card(s)`,
        });
      } else {
        toast({
          title: "Partial Deletion",
          description: `Deleted ${successCount} card(s), but ${errorCount} card(s) failed.`,
          variant: "destructive"
        });
      }
      
      // Users list feature removed - no need to refresh
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeletingUser(false);
    }
  };

  // filteredCards removed - Cards tab has been disabled (Analytics feature removed)
  // filteredUsers removed - Users tab has been disabled (Users list feature removed)

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (loading || cardLoading) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="flex items-center justify-center py-20 min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-amber-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading admin panel...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-amber-600" />
            <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
          </div>
          <p className="text-gray-600">Manage all business cards in the system</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
          <Button
            variant={activeTab === 'coupons' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('coupons')}
            className={`rounded-b-none ${activeTab === 'coupons' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Coupons ({coupons.length})
          </Button>
          <Button
            variant={activeTab === 'pricing' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('pricing')}
            className={`rounded-b-none ${activeTab === 'pricing' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Country Pricing ({countryPricings.length})
          </Button>
          <Button
            variant={activeTab === 'settings' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('settings')}
            className={`rounded-b-none ${activeTab === 'settings' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            variant={activeTab === 'chat-support' ? 'default' : 'ghost'}
            onClick={() => navigate('/admin/chat-support')}
            className={`rounded-b-none ${activeTab === 'chat-support' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Chat Support
          </Button>
          <Button
            variant={activeTab === 'card-designer' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('card-designer')}
            className={`rounded-b-none ${activeTab === 'card-designer' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <Palette className="w-4 h-4 mr-2" />
            Card Designer
          </Button>
          <Button
            variant={activeTab === 'user-designs' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('user-designs')}
            className={`rounded-b-none ${activeTab === 'user-designs' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <Users className="w-4 h-4 mr-2" />
            User Designs
          </Button>
          <Button
            variant={activeTab === 'backgrounds' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('backgrounds')}
            className={`rounded-b-none ${activeTab === 'backgrounds' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <Monitor className="w-4 h-4 mr-2" />
            Backgrounds
          </Button>
          <Button
            variant={activeTab === 'card-pricing' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('card-pricing')}
            className={`rounded-b-none ${activeTab === 'card-pricing' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Card Pricing
          </Button>
          <Button
            variant={activeTab === 'marketing' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('marketing')}
            className={`rounded-b-none ${activeTab === 'marketing' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Marketing
          </Button>
          <Button
            variant={activeTab === 'bio-templates' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('bio-templates')}
            className={`rounded-b-none ${activeTab === 'bio-templates' ? 'bg-amber-600 text-white' : 'text-gray-600'}`}
          >
            <Layers className="w-4 h-4 mr-2" />
            Bio Templates
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/nfc')}
            className="rounded-b-none text-amber-600 hover:text-amber-700 hover:bg-amber-50"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            NFC Cards
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/products')}
            className="rounded-b-none text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Shop & Orders
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/cards')}
            className="rounded-b-none text-purple-600 hover:text-purple-700 hover:bg-purple-50"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            All Cards
          </Button>
        </div>

        {/* Admin Info */}
        <div className="mb-6">
          <Card className="border-2 border-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Admin Access</p>
                  <p className="text-sm font-semibold text-blue-600 truncate">{user?.email}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        {activeTab !== 'views' && activeTab !== 'insights' && activeTab !== 'settings' && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by name, email, username, company, or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-6 text-lg border-2 border-gray-200 focus:border-amber-400"
              />
            </div>
          </div>
        )}

        {/* Users List - REMOVED (Users list feature removed) */}
        {false && activeTab === 'users' && (
          <div className="space-y-4">
            {[].length === 0 ? (
              <Card className="border-2 border-gray-100 p-12 text-center">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Users Found</h3>
                <p className="text-gray-600">
                  {searchQuery ? 'Try adjusting your search query.' : 'No users in the system yet.'}
                </p>
              </Card>
            ) : (
              [].map((userData) => (
                <Card 
                  key={userData.userId} 
                  className="border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all"
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {/* Avatar */}
                        <Avatar className="w-16 h-16 border-2 border-gray-200 flex-shrink-0">
                          <AvatarImage src={userData.userPhoto} alt={userData.userName} />
                          <AvatarFallback className="bg-gradient-to-br from-blue-400 to-amber-400 text-white text-xl">
                            {userData.userName?.charAt(0) || userData.userEmail?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>

                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {userData.userName || 'Unknown User'}
                          </h3>

                          <div className="flex items-center gap-1 text-sm text-gray-600 truncate mb-1">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{userData.userEmail}</span>
                          </div>

                          <div className="flex items-center gap-1 text-xs text-gray-400 font-mono mb-2">
                            <User className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">ID: {userData.userId}</span>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {userData.cardsCount} {userData.cardsCount === 1 ? 'Card' : 'Cards'}
                            </Badge>
                            
                            {/* Subscription Badge - Enhanced with highlighting */}
                            <Badge className={`text-sm font-bold px-3 py-1 shadow-lg ${
                              userSubscriptions[userData.userId]?.tier === 'business' 
                                ? 'bg-gradient-to-r from-teal-500 to-yellow-600 text-white ring-2 ring-teal-300 ring-offset-2'
                                : userSubscriptions[userData.userId]?.tier === 'pro'
                                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white ring-2 ring-amber-300 ring-offset-2'
                                : 'bg-gray-300 text-gray-800 ring-2 ring-gray-200 ring-offset-2'
                            }`}>
                              {userSubscriptions[userData.userId]?.tier === 'business' ? '👑 BUSINESS' :
                               userSubscriptions[userData.userId]?.tier === 'pro' ? '⚡ PRO' :
                               '🆓 FREE'}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="w-full sm:w-auto flex flex-col gap-3">
                        {/* Cards List */}
                        {userData.cards.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs text-gray-500 font-semibold mb-2">Cards:</p>
                            {userData.cards.map((card, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="truncate">
                                  {card.name || 'No Name'} (@{card.username})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Subscription Tier Selector */}
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500 font-semibold">Change Subscription:</p>
                          <div className="flex gap-2">
                            <Button
                              variant={userSubscriptions[userData.userId]?.tier === 'free' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => updateUserSubscription(userData.userId, 'free')}
                              disabled={updatingSubscription[userData.userId]}
                              className={`flex-1 text-xs font-semibold transition-all duration-200 ${
                                userSubscriptions[userData.userId]?.tier === 'free'
                                  ? 'bg-gray-500 hover:bg-gray-600 ring-2 ring-gray-300 ring-offset-2 shadow-lg scale-105'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              {updatingSubscription[userData.userId] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  {userSubscriptions[userData.userId]?.tier === 'free' && '✓ '}
                                  Free
                                </>
                              )}
                            </Button>
                            <Button
                              variant={userSubscriptions[userData.userId]?.tier === 'pro' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => updateUserSubscription(userData.userId, 'pro')}
                              disabled={updatingSubscription[userData.userId]}
                              className={`flex-1 text-xs font-semibold transition-all duration-200 ${
                                userSubscriptions[userData.userId]?.tier === 'pro'
                                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 ring-2 ring-amber-300 ring-offset-2 shadow-lg scale-105'
                                  : 'hover:bg-amber-50'
                              }`}
                            >
                              {updatingSubscription[userData.userId] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  {userSubscriptions[userData.userId]?.tier === 'pro' && '✓ '}
                                  Pro
                                </>
                              )}
                            </Button>
                            <Button
                              variant={userSubscriptions[userData.userId]?.tier === 'business' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => updateUserSubscription(userData.userId, 'business')}
                              disabled={updatingSubscription[userData.userId]}
                              className={`flex-1 text-xs font-semibold transition-all duration-200 ${
                                userSubscriptions[userData.userId]?.tier === 'business'
                                  ? 'bg-gradient-to-r from-teal-500 to-yellow-600 hover:from-teal-600 hover:to-teal-700 ring-2 ring-teal-300 ring-offset-2 shadow-lg scale-105'
                                  : 'hover:bg-teal-50'
                              }`}
                            >
                              {updatingSubscription[userData.userId] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  {userSubscriptions[userData.userId]?.tier === 'business' && '✓ '}
                                  Business
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Delete User Button */}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteUserClick(userData)}
                          className="w-full"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete User & All Cards
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Cards List - REMOVED (Analytics feature removed) */}
        {false && activeTab === 'cards' && (
          <div className="space-y-4">
          {[].length === 0 ? (
            <Card className="border-2 border-gray-100 p-12 text-center">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Cards Found</h3>
              <p className="text-gray-600">
                {searchQuery ? 'Try adjusting your search query.' : 'No cards in the system yet.'}
              </p>
            </Card>
          ) : (
            [].map((card) => (
              <Card 
                key={card._id || card.username} 
                className="border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Avatar */}
                      <Avatar className="w-16 h-16 border-2 border-gray-200 flex-shrink-0">
                        <AvatarImage src={card.profileImage} alt={card.name || card.username} />
                        <AvatarFallback className="bg-gradient-to-br from-amber-400 to-yellow-400 text-white text-xl">
                          {card.name?.charAt(0) || card.username?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>

                      {/* Card Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {card.name || 'No Name'}
                          </h3>
                          <Badge variant="outline" className="text-xs">
                            @{card.username || 'unknown'}
                          </Badge>
                        </div>

                        {/* Owner User ID */}
                        <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                          <User className="w-3 h-3 flex-shrink-0" />
                          <span className="font-mono truncate">Owner ID: {card.userId || 'N/A'}</span>
                        </div>

                        {card.title && (
                          <p className="text-sm text-gray-600 truncate">{card.title}</p>
                        )}

                        {card.company && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 truncate">
                            <Building className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{card.company}</span>
                          </div>
                        )}

                        {card.email && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 truncate mt-1">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{card.email}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                          <Calendar className="w-3 h-3" />
                          <span>Created: {formatDate(card.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/bio/${card.username}`, '_blank')}
                        className="flex-1 sm:flex-none"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteClick(card)}
                        className="flex-1 sm:flex-none"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          </div>
        )}

        {/* Views Analytics Tab */}
        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <div className="space-y-6">
            {/* Create Coupon Form */}
            <Card className="border-2 border-amber-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  Create New Coupon
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Coupon Code <span className="text-red-500">*</span>
                    </label>
                    <Input
                      placeholder="e.g., SAVE20"
                      value={newCoupon.code}
                      onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                      className="uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Percentage <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 20"
                      min="1"
                      max="100"
                      value={newCoupon.percentage}
                      onChange={(e) => setNewCoupon({...newCoupon, percentage: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Applicable Tier <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newCoupon.tier}
                      onChange={(e) => setNewCoupon({...newCoupon, tier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="all">All Tiers</option>
                      <option value="pro">Pro Only</option>
                      <option value="business">Business Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Period <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newCoupon.billingPeriod}
                      onChange={(e) => setNewCoupon({...newCoupon, billingPeriod: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="both">Both (Monthly & Annual)</option>
                      <option value="monthly">Monthly Only</option>
                      <option value="annual">Annual Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Members (Business Plan)
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 2"
                      min="1"
                      value={newCoupon.minMembers}
                      onChange={(e) => setNewCoupon({...newCoupon, minMembers: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum team members required for business plan</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Date (Optional)
                    </label>
                    <Input
                      type="date"
                      value={newCoupon.expiryDate}
                      onChange={(e) => setNewCoupon({...newCoupon, expiryDate: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Usage Limit (Optional)
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 100"
                      min="1"
                      value={newCoupon.usageLimit}
                      onChange={(e) => setNewCoupon({...newCoupon, usageLimit: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Order Value ₹ (Optional)
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 500"
                      min="0"
                      value={newCoupon.minOrderValue}
                      onChange={(e) => setNewCoupon({...newCoupon, minOrderValue: e.target.value})}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description (Optional)
                    </label>
                    <Input
                      placeholder="e.g., Special offer for new users"
                      value={newCoupon.description}
                      onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                    />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="oneTimePerUser"
                      checked={newCoupon.oneTimePerUser}
                      onChange={(e) => setNewCoupon({...newCoupon, oneTimePerUser: e.target.checked})}
                      className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    <label htmlFor="oneTimePerUser" className="text-sm text-gray-700">
                      One-time use per user (each user can use this coupon only once)
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="couponActive"
                      checked={newCoupon.isActive}
                      onChange={(e) => setNewCoupon({...newCoupon, isActive: e.target.checked})}
                      className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    <label htmlFor="couponActive" className="text-sm text-gray-700">
                      Active (coupon can be used immediately)
                    </label>
                  </div>
                </div>
                <Button
                  onClick={createCoupon}
                  className="mt-6 w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
                >
                  Create Coupon
                </Button>
              </CardContent>
            </Card>

            {/* Coupons List */}
            <Card className="border-2 border-gray-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  All Coupons ({coupons.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingCoupons ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 text-amber-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading coupons...</p>
                  </div>
                ) : coupons.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No coupons created yet</p>
                    <p className="text-sm text-gray-400">Create your first coupon above</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {coupons.map((coupon) => {
                      const isExpired = coupon.expiryDate && new Date(coupon.expiryDate) < new Date();
                      const usageLimitReached = coupon.usageLimit && coupon.usedCount >= coupon.usageLimit;
                      
                      return (
                        <div key={coupon.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-amber-300 transition-all">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <span className="text-lg font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-lg">
                                  {coupon.code}
                                </span>
                                <Badge className="bg-gradient-to-r from-green-500 to-yellow-500 text-white">
                                  {coupon.percentage}% OFF
                                </Badge>
                                <Badge className={`${
                                  coupon.tier === 'pro' ? 'bg-amber-100 text-amber-700' :
                                  coupon.tier === 'business' ? 'bg-teal-100 text-teal-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {coupon.tier === 'pro' ? 'Pro' : 
                                   coupon.tier === 'business' ? 'Business' : 
                                   'All Tiers'}
                                </Badge>
                                <Badge className="bg-orange-100 text-orange-700">
                                  {coupon.billingPeriod === 'both' ? 'Monthly & Annual' :
                                   coupon.billingPeriod === 'monthly' ? 'Monthly Only' :
                                   'Annual Only'}
                                </Badge>
                                {coupon.isActive && !isExpired && !usageLimitReached ? (
                                  <Badge className="bg-green-100 text-green-700">Active</Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                                )}
                                {isExpired && <Badge className="bg-gray-100 text-gray-700">Expired</Badge>}
                                {usageLimitReached && <Badge className="bg-gray-100 text-gray-700">Limit Reached</Badge>}
                              </div>
                              {coupon.description && (
                                <p className="text-sm text-gray-600 mb-2">{coupon.description}</p>
                              )}
                              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                                {coupon.minMembers && (
                                  <span className="font-semibold text-amber-600">Min {coupon.minMembers} Members</span>
                                )}
                                {coupon.minOrderValue && (
                                  <span>Min Order: ₹{coupon.minOrderValue}</span>
                                )}
                                {coupon.usageLimit && (
                                  <span>Usage: {coupon.usedCount}/{coupon.usageLimit}</span>
                                )}
                                {!coupon.usageLimit && coupon.usedCount > 0 && (
                                  <span>Used: {coupon.usedCount} times</span>
                                )}
                                {coupon.oneTimePerUser && (
                                  <span className="font-semibold text-blue-600">🔒 One-time per user</span>
                                )}
                                {coupon.expiryDate && (
                                  <span>Expires: {new Date(coupon.expiryDate).toLocaleDateString()}</span>
                                )}
                                <span>Created: {formatDate(coupon.createdAt)}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleCouponStatus(coupon.id, coupon.isActive)}
                                className={coupon.isActive ? 'border-orange-300 text-orange-600 hover:bg-orange-50' : 'border-green-300 text-green-600 hover:bg-green-50'}
                              >
                                {coupon.isActive ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  if (window.confirm(`Delete coupon "${coupon.code}"?`)) {
                                    deleteCoupon(coupon.id);
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Country Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            {/* Create/Edit Country Pricing Form */}
            <Card className="border-2 border-amber-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-amber-600" />
                  {editingCountry ? 'Edit Country Pricing' : 'Add Country Pricing'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country Code <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">(2-letter ISO code, e.g., US, GB, IN or ROW)</span>
                    </label>
                    <Input
                      placeholder="e.g., US or ROW"
                      value={newCountryPricing.countryCode}
                      onChange={(e) => setNewCountryPricing({...newCountryPricing, countryCode: e.target.value.toUpperCase()})}
                      className="uppercase"
                      maxLength={3}
                      disabled={!!editingCountry}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      placeholder="e.g., United States"
                      value={newCountryPricing.countryName}
                      onChange={(e) => setNewCountryPricing({...newCountryPricing, countryName: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pro Monthly (₹) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 199"
                      min="0"
                      value={newCountryPricing.proMonthly}
                      onChange={(e) => setNewCountryPricing({...newCountryPricing, proMonthly: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pro Annual (₹) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 1990"
                      min="0"
                      value={newCountryPricing.proAnnual}
                      onChange={(e) => setNewCountryPricing({...newCountryPricing, proAnnual: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Monthly (₹) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 599"
                      min="0"
                      value={newCountryPricing.businessMonthly}
                      onChange={(e) => setNewCountryPricing({...newCountryPricing, businessMonthly: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Annual (₹) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g., 5900"
                      min="0"
                      value={newCountryPricing.businessAnnual}
                      onChange={(e) => setNewCountryPricing({...newCountryPricing, businessAnnual: e.target.value})}
                    />
                  </div>
                </div>
                <div className="mt-6 flex gap-2">
                  <Button
                    onClick={createOrUpdateCountryPricing}
                    className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
                  >
                    {editingCountry ? 'Update Pricing' : 'Add Country Pricing'}
                  </Button>
                  {editingCountry && (
                    <Button
                      onClick={cancelEdit}
                      variant="outline"
                      className="border-2"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Country Pricing List */}
            <Card className="border-2 border-gray-100">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-teal-600" />
                    All Country Pricing ({countryPricings.length})
                  </span>
                  <Input
                    placeholder="Search countries..."
                    value={countrySearchQuery}
                    onChange={(e) => setCountrySearchQuery(e.target.value)}
                    className="max-w-xs"
                  />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingCountryPricing ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 text-amber-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading country pricing...</p>
                  </div>
                ) : countryPricings.length === 0 ? (
                  <div className="text-center py-12">
                    <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No country pricing configured yet</p>
                    <p className="text-sm text-gray-400">Add your first country pricing above</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b-2 border-gray-200">
                        <tr className="text-left">
                          <th className="py-3 px-4 font-semibold text-gray-700">Country</th>
                          <th className="py-3 px-4 font-semibold text-gray-700">Pro Monthly</th>
                          <th className="py-3 px-4 font-semibold text-gray-700">Pro Annual</th>
                          <th className="py-3 px-4 font-semibold text-gray-700">Business Monthly</th>
                          <th className="py-3 px-4 font-semibold text-gray-700">Business Annual</th>
                          <th className="py-3 px-4 font-semibold text-gray-700">Status</th>
                          <th className="py-3 px-4 font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countryPricings
                          .filter(country => 
                            country.countryName.toLowerCase().includes(countrySearchQuery.toLowerCase()) ||
                            country.countryCode.toLowerCase().includes(countrySearchQuery.toLowerCase())
                          )
                          .map((country) => (
                            <tr key={country.countryCode} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-amber-100 text-amber-700 font-mono">
                                    {country.countryCode}
                                  </Badge>
                                  <span className="font-medium">{country.countryName}</span>
                                </div>
                              </td>
                              <td className="py-4 px-4">₹{(country.proMonthly / 100).toFixed(0)}</td>
                              <td className="py-4 px-4">₹{(country.proAnnual / 100).toFixed(0)}</td>
                              <td className="py-4 px-4">₹{(country.businessMonthly / 100).toFixed(0)}</td>
                              <td className="py-4 px-4">₹{(country.businessAnnual / 100).toFixed(0)}</td>
                              <td className="py-4 px-4">
                                <Badge className={country.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {country.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="py-4 px-4">
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => editCountryPricing(country)}
                                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                  >
                                    Edit
                                  </Button>
                                  {country.countryCode !== 'IN' && country.countryCode !== 'ROW' && (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => {
                                        if (window.confirm(`Delete pricing for ${country.countryName}?`)) {
                                          deleteCountryPricing(country.countryCode);
                                        }
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Assign Cards Tab */}
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <Card className="border-2 border-gray-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-amber-600" />
                  Application Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pricing Toggle */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-teal-50 rounded-lg border-2 border-amber-100">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Pricing Page
                    </h3>
                    <p className="text-sm text-gray-600">
                      {pricingEnabled 
                        ? 'Pricing page is visible and accessible to all users'
                        : 'Pricing page shows "Coming Soon" message and menu link is hidden'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={async () => {
                        setSavingSettings(true);
                        try {
                          const newState = !pricingEnabled;
                          // Store in localStorage for now (can be moved to backend later)
                          localStorage.setItem('pricingEnabled', JSON.stringify(newState));
                          setPricingEnabled(newState);
                          toast({
                            title: "Settings Updated",
                            description: `Pricing page is now ${newState ? 'enabled' : 'disabled'}.`,
                          });
                        } catch (error) {
                          toast({
                            title: "Error",
                            description: "Failed to update settings.",
                            variant: "destructive"
                          });
                        } finally {
                          setSavingSettings(false);
                        }
                      }}
                      disabled={savingSettings}
                      className={`${
                        pricingEnabled
                          ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                          : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                      } text-white`}
                    >
                      {savingSettings ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : null}
                      {pricingEnabled ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                </div>

                {/* Premium Membership Toggle */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-2 border-orange-100">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Premium Membership
                    </h3>
                    <p className="text-sm text-gray-600">
                      {premiumEnabled 
                        ? 'Premium features require subscription (analytics gated)'
                        : 'All features accessible to everyone (no subscription needed)'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={async () => {
                        setSavingSettings(true);
                        try {
                          const newState = !premiumEnabled;
                          localStorage.setItem('premiumEnabled', JSON.stringify(newState));
                          setPremiumEnabled(newState);
                          toast({
                            title: "Settings Updated",
                            description: `Premium membership is now ${newState ? 'enabled' : 'disabled'}.`,
                          });
                        } catch (error) {
                          toast({
                            title: "Error",
                            description: "Failed to update settings.",
                            variant: "destructive"
                          });
                        } finally {
                          setSavingSettings(false);
                        }
                      }}
                      disabled={savingSettings}
                      className={`${
                        premiumEnabled
                          ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                          : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                      } text-white`}
                    >
                      {savingSettings ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : null}
                      {premiumEnabled ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                </div>

                {/* Scan Limit Settings */}
                <div className="p-4 bg-gradient-to-r from-blue-50 to-amber-50 rounded-lg border-2 border-blue-100">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Scan Limit Configuration
                    </h3>
                    <p className="text-sm text-gray-600">
                      Control how many business cards each user can scan
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Scan Credits Information */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <Scan className="h-5 w-5 text-blue-500" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">
                            Scan Credits
                          </h3>
                          <div className="mt-2 text-sm text-blue-700">
                            <p>All users receive:</p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              <li><strong>10 Physical Card Scans</strong> (Free)</li>
                              <li><strong>10 CRM Records</strong> (Free)</li>
                            </ul>
                            <p className="mt-2">Pro and Business plans include unlimited scans and CRM records.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Card */}
                <Card className="border-2 border-blue-100 bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-1">About Settings Control</h4>
                        <p className="text-sm text-blue-700 mb-2">
                          <strong>Pricing Control:</strong> When disabled, the Pricing menu link is hidden and the page shows a "Coming Soon" message instead of pricing plans.
                        </p>
                        <p className="text-sm text-blue-700">
                          <strong>Premium Membership:</strong> When disabled, all premium features (like analytics) become accessible to all users without requiring a subscription.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </div>
        )}


        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                Delete Card?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this card?
                <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="font-semibold text-gray-900">
                    {cardToDelete?.name || 'No Name'}
                  </p>
                  <p className="text-sm text-gray-600">@{cardToDelete?.username || 'unknown'}</p>
                  <p className="text-xs text-gray-500 font-mono mt-1">
                    User ID: {cardToDelete?.userId || 'N/A'}
                  </p>
                </div>
                <p className="mt-3">This action cannot be undone and will permanently remove:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Business card information</li>
                  <li>All analytics data</li>
                  <li>Associated contacts</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Card
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete User Confirmation Dialog */}
        <AlertDialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                Delete User & All Data?
              </AlertDialogTitle>
              <AlertDialogDescription>
                <div className="space-y-3">
                  <p className="font-semibold text-red-600">⚠️ WARNING: This is a destructive action!</p>
                  
                  <div className="p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="font-semibold text-gray-900">
                      {userToDelete?.userName || 'Unknown User'}
                    </p>
                    <p className="text-sm text-gray-600">{userToDelete?.userEmail}</p>
                    <p className="text-xs text-gray-500 font-mono mt-1">
                      User ID: {userToDelete?.userId || 'N/A'}
                    </p>
                    <Badge variant="destructive" className="mt-2">
                      {userToDelete?.cardsCount} Card(s) will be deleted
                    </Badge>
                  </div>

                  <p className="text-sm">This will permanently delete:</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li><strong>All {userToDelete?.cardsCount} business card(s)</strong></li>
                    <li>All analytics data for their cards</li>
                    <li>All contacts saved by this user</li>
                    <li>All QR codes and card views</li>
                  </ul>

                  {userToDelete?.cards && userToDelete.cards.length > 0 && (
                    <div className="p-2 bg-red-50 rounded border border-red-200">
                      <p className="text-xs font-semibold text-red-800 mb-1">Cards to be deleted:</p>
                      {userToDelete.cards.map((card, idx) => (
                        <p key={idx} className="text-xs text-red-700">
                          • {card.name || 'No Name'} (@{card.username})
                        </p>
                      ))}
                    </div>
                  )}

                  <p className="text-sm font-semibold text-red-600">
                    This action cannot be undone!
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingUser}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUserConfirm}
                disabled={deletingUser}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingUser ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User & All Cards
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ============ CARD DESIGNER TAB ============ */}
        {activeTab === 'card-designer' && (
          <div className="-mx-4 -mb-6">
            <AdminCardDesigner embedded />
          </div>
        )}

        {activeTab === 'user-designs' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">User Customizations</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Includes auto-saved drafts (live edits even before user clicks Save)
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={loadUserDesigns} disabled={loadingDesigns}>
                {loadingDesigns ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            {loadingDesigns ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /></div>
            ) : userDesigns.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No user designs yet</div>
            ) : (
              <div className="space-y-3">
                {userDesigns.map(d => (
                  <div key={d.id} className={`bg-white border rounded-lg p-4 flex gap-4 items-start ${d.is_draft ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`user-design-${d.id}`}>
                    {/* Preview */}
                    <div className="w-52 flex-shrink-0 space-y-1.5">
                      <div className="rounded-lg overflow-hidden border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => { setPreviewDesign(d); setPreviewSide('front'); }}>
                        <DesignCardPreview design={d} side="front" />
                      </div>
                      <button className="w-full text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center justify-center gap-1 py-1"
                        onClick={() => { setPreviewDesign(d); setPreviewSide('front'); }} data-testid={`preview-design-${d.id}`}>
                        <Eye className="w-3 h-3" />Preview
                      </button>
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-800 text-sm">{d.template_name || 'Custom Design'}</h3>
                            {/* Draft / Auto-saved badge */}
                            {d.is_draft ? (
                              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                Draft · Auto-saved
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Saved
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {d.is_draft ? 'Last auto-saved' : 'Saved'}: {new Date(d.updated_at || d.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 flex-shrink-0" onClick={() => deleteUserDesign(d.id)} data-testid={`delete-design-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="mt-3 bg-gray-50 rounded-lg p-3 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">{d.user_display_name || 'Unknown User'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FileText className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-600">{d.user_email || '—'}</span>
                        </div>
                        {d.user_phone && (
                          <div className="flex items-center gap-2">
                            <Activity className="w-3.5 h-3.5 text-gray-400" />
                            <a href={`tel:${d.user_phone}`} className="text-sm text-amber-600 font-medium hover:underline">{d.user_phone}</a>
                          </div>
                        )}
                      </div>
                      {/* Text preview */}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(d.text_elements || d.front_text_elements || []).slice(0, 4).map((el, i) => (
                          <span key={i} className="inline-block bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded truncate max-w-[140px]">{el.text || el.content || ''}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Admin Design Preview Modal */}
            <Dialog open={!!previewDesign} onOpenChange={(open) => { if (!open) setPreviewDesign(null); }}>
              <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
                <DialogHeader className="px-5 pt-5 pb-0">
                  <DialogTitle className="text-base">
                    {previewDesign?.template_name || 'Design Preview'}
                    {previewDesign?.user_display_name && (
                      <span className="text-sm font-normal text-gray-500 ml-2">by {previewDesign.user_display_name}</span>
                    )}
                    {previewDesign?.is_draft && (
                      <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Draft</span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="p-5 space-y-3">
                  <div className="flex justify-center gap-1">
                    <button onClick={() => setPreviewSide('front')}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${previewSide === 'front' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}>Front</button>
                    <button onClick={() => setPreviewSide('back')}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${previewSide === 'back' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}>Back</button>
                  </div>
                  {previewDesign && (
                    <div className="rounded-xl overflow-hidden shadow-xl border max-w-lg mx-auto">
                      <DesignCardPreview design={previewDesign} side={previewSide} />
                    </div>
                  )}
                  {previewDesign && (
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 max-w-lg mx-auto">
                      <div className="flex justify-between">
                        <span>{previewDesign.user_email}</span>
                        {previewDesign.user_phone && <a href={`tel:${previewDesign.user_phone}`} className="text-amber-600 font-medium">{previewDesign.user_phone}</a>}
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Backgrounds Tab */}
        {activeTab === 'backgrounds' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Meeting Backgrounds</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Manage virtual meeting backgrounds for users
                </p>
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadBackground(file);
                      e.target.value = '';
                    }}
                    disabled={uploadingBackground}
                  />
                  <Button variant="default" className="bg-amber-600 hover:bg-amber-700" asChild disabled={uploadingBackground}>
                    <span>
                      {uploadingBackground ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Upload New
                    </span>
                  </Button>
                </label>
                <Button variant="outline" size="sm" onClick={loadAdminBackgrounds} disabled={loadingBackgrounds}>
                  {loadingBackgrounds ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
            </div>
            
            {loadingBackgrounds ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" /></div>
            ) : adminBackgrounds.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No backgrounds yet</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {adminBackgrounds.map(bg => (
                  <div key={bg.id} className="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    {/* Preview */}
                    <div className="aspect-video relative">
                      {bg.type === 'gradient' ? (
                        <div className="w-full h-full" style={{ background: bg.gradient }} />
                      ) : (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${bg.thumbnail_url}`}
                          alt={bg.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {bg.is_premium && (
                        <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">
                          Premium
                        </span>
                      )}
                      {bg.is_default && (
                        <span className="absolute top-2 left-2 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">
                          Default
                        </span>
                      )}
                    </div>
                    {/* Info */}
                    <div className="p-3">
                      <h3 className="font-medium text-sm text-gray-800 truncate">{bg.name}</h3>
                      <p className="text-xs text-gray-500 capitalize">{bg.category}</p>
                      {!bg.is_default && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete "${bg.name}"?`)) {
                              deleteBackground(bg.id);
                            }
                          }}
                          className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Card Pricing Tab */}
        {activeTab === 'card-pricing' && (
          <div className="space-y-6">
            {/* NFC Card Pricing */}
            <Card className="border-2 border-amber-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  NFC Card Pricing (All Custom Designs)
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Set a single price for all NFC cards regardless of design template
                </p>
              </CardHeader>
              <CardContent>
                {loadingCardPricing ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-amber-600 animate-spin mx-auto" />
                    <p className="text-gray-500 mt-2">Loading pricing...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        MRP (₹) <span className="text-gray-400 text-xs">(Original Price)</span>
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={cardPricing.nfc_mrp}
                        onChange={(e) => setCardPricing({...cardPricing, nfc_mrp: parseFloat(e.target.value) || 0})}
                        className="text-lg font-semibold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Selling Price (₹) <span className="text-green-600 text-xs">(Customer Pays)</span>
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={cardPricing.nfc_price}
                        onChange={(e) => setCardPricing({...cardPricing, nfc_price: parseFloat(e.target.value) || 0})}
                        className="text-lg font-semibold border-green-200 focus:border-green-400"
                      />
                    </div>
                    {cardPricing.nfc_mrp > cardPricing.nfc_price && (
                      <div className="col-span-2 bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-700">
                          <span className="font-semibold">Discount: </span>
                          {Math.round(((cardPricing.nfc_mrp - cardPricing.nfc_price) / cardPricing.nfc_mrp) * 100)}% OFF 
                          <span className="text-green-600 ml-2">(Save ₹{cardPricing.nfc_mrp - cardPricing.nfc_price})</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Paper Cards Pricing */}
            <Card className="border-2 border-violet-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-600" />
                  Paper/Printed Cards Pricing (Quantity-based)
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Set prices for different quantities of printed paper business cards
                </p>
              </CardHeader>
              <CardContent>
                {loadingCardPricing ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-violet-600 animate-spin mx-auto" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="space-y-2 p-4 bg-violet-50 rounded-lg border border-violet-200">
                      <label className="block text-sm font-semibold text-violet-800">100 Cards</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <Input
                          type="number"
                          min="0"
                          value={cardPricing.paper_100}
                          onChange={(e) => setCardPricing({...cardPricing, paper_100: parseFloat(e.target.value) || 0})}
                          className="pl-8 font-semibold"
                        />
                      </div>
                      <p className="text-xs text-violet-600">₹{(cardPricing.paper_100/100).toFixed(2)}/card</p>
                    </div>
                    <div className="space-y-2 p-4 bg-violet-50 rounded-lg border border-violet-200">
                      <label className="block text-sm font-semibold text-violet-800">200 Cards</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <Input
                          type="number"
                          min="0"
                          value={cardPricing.paper_200}
                          onChange={(e) => setCardPricing({...cardPricing, paper_200: parseFloat(e.target.value) || 0})}
                          className="pl-8 font-semibold"
                        />
                      </div>
                      <p className="text-xs text-violet-600">₹{(cardPricing.paper_200/200).toFixed(2)}/card</p>
                    </div>
                    <div className="space-y-2 p-4 bg-violet-50 rounded-lg border border-violet-200">
                      <label className="block text-sm font-semibold text-violet-800">300 Cards</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <Input
                          type="number"
                          min="0"
                          value={cardPricing.paper_300}
                          onChange={(e) => setCardPricing({...cardPricing, paper_300: parseFloat(e.target.value) || 0})}
                          className="pl-8 font-semibold"
                        />
                      </div>
                      <p className="text-xs text-violet-600">₹{(cardPricing.paper_300/300).toFixed(2)}/card</p>
                    </div>
                    <div className="space-y-2 p-4 bg-violet-50 rounded-lg border border-violet-200">
                      <label className="block text-sm font-semibold text-violet-800">500 Cards</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <Input
                          type="number"
                          min="0"
                          value={cardPricing.paper_500}
                          onChange={(e) => setCardPricing({...cardPricing, paper_500: parseFloat(e.target.value) || 0})}
                          className="pl-8 font-semibold"
                        />
                      </div>
                      <p className="text-xs text-violet-600">₹{(cardPricing.paper_500/500).toFixed(2)}/card</p>
                    </div>
                    <div className="space-y-2 p-4 bg-violet-50 rounded-lg border border-violet-200">
                      <label className="block text-sm font-semibold text-violet-800">1000 Cards</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <Input
                          type="number"
                          min="0"
                          value={cardPricing.paper_1000}
                          onChange={(e) => setCardPricing({...cardPricing, paper_1000: parseFloat(e.target.value) || 0})}
                          className="pl-8 font-semibold"
                        />
                      </div>
                      <p className="text-xs text-violet-600">₹{(cardPricing.paper_1000/1000).toFixed(2)}/card</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={saveCardPricing}
                disabled={savingCardPricing || loadingCardPricing}
                className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white px-8"
              >
                {savingCardPricing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save All Pricing'
                )}
              </Button>
            </div>

            {/* Preview Card */}
            <Card className="border-2 border-gray-100 bg-gray-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-700">
                  <Eye className="w-5 h-5" />
                  Pricing Preview (How it appears to customers)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* NFC Preview */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-lg text-gray-900">NFC Business Card</h3>
                      <Badge className="bg-amber-100 text-amber-700">Any Design</Badge>
                    </div>
                    <div className="flex items-baseline gap-2">
                      {cardPricing.nfc_mrp > cardPricing.nfc_price && (
                        <span className="text-gray-400 line-through text-lg">₹{cardPricing.nfc_mrp}</span>
                      )}
                      <span className="text-3xl font-bold text-amber-600">₹{cardPricing.nfc_price}</span>
                    </div>
                    {cardPricing.nfc_mrp > cardPricing.nfc_price && (
                      <p className="text-sm text-green-600 font-medium mt-1">
                        {Math.round(((cardPricing.nfc_mrp - cardPricing.nfc_price) / cardPricing.nfc_mrp) * 100)}% OFF
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">Tap-to-share smart NFC card</p>
                  </div>
                  
                  {/* Paper Cards Preview */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="font-bold text-lg text-gray-900 mb-4">Printed Paper Cards</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="text-gray-600">100 cards</span>
                        <span className="font-semibold text-violet-600">₹{cardPricing.paper_100}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="text-gray-600">200 cards</span>
                        <span className="font-semibold text-violet-600">₹{cardPricing.paper_200}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="text-gray-600">300 cards</span>
                        <span className="font-semibold text-violet-600">₹{cardPricing.paper_300}</span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="text-gray-600">500 cards</span>
                        <span className="font-semibold text-violet-600">₹{cardPricing.paper_500}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-600">1000 cards</span>
                        <span className="font-semibold text-violet-600">₹{cardPricing.paper_1000}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Marketing Tab */}
        {activeTab === 'marketing' && (
          <AdminMarketing />
        )}

        {/* Bio Templates Tab */}
        {activeTab === 'bio-templates' && (
          <AdminBioTemplates />
        )}



      </div>
    </Layout>
  );
};

export default Admin;
